import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { LOGO, scrollToSection } from "../../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What services does RCS offer?",
  "Tell me about the School SaaS",
  "How do I start a project?",
];

const getSessionId = () => {
  let id = localStorage.getItem("rcs_chat_session");
  if (!id) {
    id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("rcs_chat_session", id);
  }
  return id;
};

const renderRich = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="font-semibold text-navy">{part.slice(2, -2)}</strong>
      : part
  );

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm the RCS Assistant. Ask me anything about our services, School Management SaaS, or how to start your project." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message }),
      });
      if (!res.ok || !res.body) throw new Error("request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + payload.delta };
              return copy;
            });
          }
          if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: payload.error, error: true };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "The assistant is unavailable right now. Please use the enquiry form below and we'll get back to you.", error: true };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.6, duration: 0.5 }}
        onClick={() => setOpen(!open)}
        data-testid="chat-open-button"
        aria-label={open ? "Close RCS assistant" : "Open RCS AI assistant"}
        className="fixed bottom-6 right-6 z-50 flex h-14 items-center gap-2 rounded-full bg-navy px-4 text-white shadow-[0_16px_40px_-10px_rgba(17,24,39,0.55)] ring-1 ring-gold/40 transition-colors duration-300 hover:bg-gold hover:text-navy"
      >
        {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5 text-gold-light" />}
        {!open && <span className="hidden font-jakarta text-[13px] font-semibold sm:inline">Ask RCS</span>}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-4 bottom-24 z-50 flex h-[480px] max-h-[70vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_40px_90px_-30px_rgba(17,24,39,0.45)] sm:inset-x-auto sm:right-6 sm:w-[380px]"
            data-testid="chat-panel"
            role="dialog"
            aria-label="RCS AI assistant"
          >
            <div className="flex items-center gap-3 bg-navy px-4 py-3.5">
              <img src={LOGO} alt="RCS logo" className="h-9 w-9 rounded-full object-cover ring-1 ring-gold/50" />
              <div className="flex-1">
                <p className="font-jakarta text-[13.5px] font-bold text-white">RCS Assistant</p>
                <p className="font-mono2 text-[8.5px] uppercase tracking-[0.2em] text-gold-light">AI · Answers about RCS</p>
              </div>
              <span className="flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 font-mono2 text-[9px] text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 pulse-dot" /> Online
              </span>
            </div>

            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto bg-mist px-4 py-4" data-testid="chat-message-list">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] whitespace-pre-line rounded-xl px-3.5 py-2.5 font-inter text-[13px] leading-relaxed ${
                      m.role === "user"
                        ? "bg-navy text-white"
                        : m.error
                          ? "border border-red-200 bg-red-50 text-red-600"
                          : "border border-slate-200 bg-white text-slate-700"
                    }`}
                    data-testid={`chat-message-${m.role}-${i}`}
                  >
                    {m.text
                      ? m.role === "assistant" && !m.error ? renderRich(m.text) : m.text
                      : streaming && i === messages.length - 1 ? (
                        <span className="flex items-center gap-1.5 text-slate-400">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                        </span>
                      ) : m.text}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 border-t border-slate-100 bg-white px-4 py-3">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid={`chat-suggestion-${i + 1}`}
                    className="rounded-full border border-slate-200 bg-mist px-3 py-1.5 font-inter text-[11.5px] text-slate-600 transition-colors duration-300 hover:border-gold hover:text-gold"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 border-t border-slate-200 bg-white px-3 py-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="chat-input"
                placeholder="Ask about services, School SaaS…"
                maxLength={1000}
                aria-label="Message the RCS assistant"
                className="flex-1 rounded-full border border-slate-200 bg-mist px-4 py-2.5 font-inter text-[13px] text-navy placeholder:text-slate-400 focus:border-gold"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Send message"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy text-gold-light transition-colors duration-300 hover:bg-gold hover:text-navy disabled:opacity-50"
              >
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
            <button
              onClick={() => { setOpen(false); scrollToSection("#contact"); }}
              data-testid="chat-consultation-link"
              className="border-t border-slate-100 bg-white py-2.5 text-center font-mono2 text-[9.5px] uppercase tracking-[0.22em] text-slate-400 transition-colors duration-300 hover:text-gold"
            >
              Prefer a human? Get a free consultation
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
